// ./src/pages/private/HomePage/HomePage.tsx
//
// The first authenticated screen in the product, and the first tab on a phone (ROADMAP A2b #50).
// Until 2026-09-25 it said "Your events dashboard will appear here."
//
// It answers ONE question — **what needs me today, across everything** — which no per-event page
// answers. ⚠ It is deliberately NOT the event dashboard at account scope: the detail belongs to the
// event, one click away, and a second copy of it here would only be a worse version of a page that
// already exists.
//
// ⚠⚠ EVERY WAY THIS PAGE CAN BE WRONG LOOKS LIKE GOOD NEWS — a failed count, an unanswered source
// and a genuine zero all render as nothing. The rules that decide what it may claim live in
// `@/utils/homeOverview` and are RUN by `npm run check-home`; a page that renders is not evidence.

import React, { useCallback, useMemo, useState } from 'react';
import { Grid, Group, Stack, Text, Title } from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageProps } from '@sdflc/react-router-pages';

import { USER_ROLES, getRoutePath } from '@/config';
import { useApp, useAppEnvironment, useOptionalAccountPreferenceContext } from '@/contexts';
import { useOptionalOrganizerProfilesContext } from '@/contexts/OrganizerProfilesContext/OrganizerProfilesContext';
import { useAccountType, useEntityPageShortcuts, useEntityPageShortcutHints, useHomeOverview, SHORTCUT_HINTS } from '@/hooks';
import { CrewSessionService, Event } from '@/utils/api';
import { buildChecklist, checklistComplete, hasPublishedEvent, sellsPaidFrom } from '@/utils/homeOverview';
import { VENUE_ACCOUNT_TYPE } from '@/components/features/EventSetup';
import { PageChrome, MobileHeaderAction } from "@/components/layouts";
import { ContentLoadingOverlay, ErrorAlert, PullToRefresh, ShortcutHintBar } from '@/components/generic';

import {
  AttentionWidget,
  ChecklistWidget,
  CrewHome,
  HALF,
  HomeFirstRun,
  HomeGridSkeleton,
  HomeSpotlight,
  HomeTasksWidget,
  HomeWeatherWidget,
  UpcomingWidget,
} from './homeWidgets';

/** How many other upcoming events the "Coming up" card lists beside the spotlight. */
const UPCOMING_LIMIT = 4;

/** Per-viewer, per-user dismissal of the getting-started card. A convenience, not state. */
const dismissKey = (userId: string): string => `em.home.checklist.dismissed.${userId}`;

const readDismissed = (userId: string): boolean => {
  try {
    return window.localStorage.getItem(dismissKey(userId)) === '1';
  } catch {
    return false;
  }
};

export const HomePage: React.FC<PageProps> = () => {
  const { t } = useTranslation(['home', 'common', 'events']);
  const { isMobile } = useAppEnvironment();
  const navigate = useNavigate();
  const { user } = useApp();

  const userId = user?.id ?? '';
  const isCrewSession = user?.userRole?.id === USER_ROLES.CREW;
  // #198 — the organizer-page item is the owner's (the onboarding step it opens is offered to the owner only).
  const isOwner = user?.userRole?.id === USER_ROLES.OWNER;

  const [checklistDismissed, setChecklistDismissed] = useState<boolean>(() => (userId ? readDismissed(userId) : false));

  const {
    events,
    upcoming,
    eventsLoading,
    eventsLoaded,
    eventsError,
    spotlight,
    spotlightStats,
    attentionSummary,
    homeWeather,
    tasks,
    tasksLoading,
    tasksLoaded,
    payoutsReady,
    refreshing,
    loading,
    reload,
  } = useHomeOverview({
    // ⚠ `!!userId` IS NOT BELT-AND-BRACES. A cold load resolves the signed-in user asynchronously,
    // and firing these before it answers sends unauthenticated list calls that come back refused —
    // which this page renders as three failed sources on the first screen of the product.
    enabled: !isCrewSession && !!userId,
    wantsPayoutStatus: !isCrewSession && !!userId && !checklistDismissed,
  });

  // ROADMAP #161 d — a venue account's "save your venue" step: what the account runs, read only while the card is wanted.
  const preferences = useOptionalAccountPreferenceContext();
  const accountId = user?.account?.id ?? '';
  const { accountType } = useAccountType(accountId, !isCrewSession && !checklistDismissed);
  // #198 b — the account's organizer pages (app-wide, read whole).
  const organizerPages = useOptionalOrganizerProfilesContext();

  const goTo = useCallback((path: string) => navigate(path), [navigate]);
  const goToCreate = useCallback(() => navigate(getRoutePath('events-create')), [navigate]);
  const goToEvents = useCallback(() => navigate(getRoutePath('events')), [navigate]);
  const openEvent = useCallback((event: Event) => event.id && navigate(`/events/${event.id}`), [navigate]);

  const checklist = useMemo(
    () =>
      buildChecklist({
        emailConfirmed: user?.email ? user.emailConfirmation === true : null,
        hasEvents: events.length > 0,
        hasPublishedEvent: hasPublishedEvent(events),
        payoutsReady,
        sellsPaid: sellsPaidFrom(attentionSummary),
        isVenueAccount: accountType === undefined ? null : accountType === VENUE_ACCOUNT_TYPE,
        hasDefaultVenue: preferences?.isLoaded ? !!preferences.preference?.defaultVenueId : null,
        // ⓘ The OWNER's (admins are not offered the onboarding step this opens); a failed read is unknown — left out.
        organizerPageReady:
          isOwner && organizerPages?.isLoaded && !organizerPages.loadFailed
            ? organizerPages.organizerProfiles.some((page) => !!page.logoUploadedFileId)
            : null,
      }),
    [
      user?.email,
      user?.emailConfirmation,
      events,
      payoutsReady,
      attentionSummary,
      accountType,
      preferences?.isLoaded,
      preferences?.preference?.defaultVenueId,
      isOwner,
      organizerPages?.isLoaded,
      organizerPages?.loadFailed,
      organizerPages?.organizerProfiles,
    ],
  );

  // The card retires itself once every step is done — a permanent checklist on the most-tapped
  // screen in the app is a permanent reminder of nothing.
  const showChecklist = !isCrewSession && !checklistDismissed && !checklistComplete(checklist);

  const dismissChecklist = useCallback(() => {
    setChecklistDismissed(true);

    try {
      window.localStorage.setItem(dismissKey(userId), '1');
    } catch {
      // A browser that refuses storage simply shows the card again next time.
    }
  }, [userId]);

  // eventId → title, so a task can name its event without a second query.
  const eventTitles = useMemo(() => {
    const map: Record<string, string> = {};

    for (const event of events) {
      if (event.id) {
        map[event.id] = event.title ?? '';
      }
    }

    return map;
  }, [events]);

  const otherUpcoming = useMemo(
    () => upcoming.filter((event) => event.id !== spotlight.event?.id).slice(0, UPCOMING_LIMIT),
    [upcoming, spotlight.event],
  );

  const mobileHeaderActions = useMemo(
    (): MobileHeaderAction[] => [
      // ⚠ Desktop/tablet only — phones pull to refresh. The same corner every list page uses.
      ...(isMobile
        ? []
        : [
            {
              iconName: 'Refresh',
              tooltip: `${t('common:buttons.refresh', { defaultValue: 'Refresh' })} · ${SHORTCUT_HINTS.refresh}`,
              variant: 'subtle' as const,
              disabled: loading,
              onClick: reload,
            },
          ]),
      {
        iconName: 'CalendarPlus',
        tooltip: isMobile
          ? t('events:list.create', { defaultValue: 'Create event' })
          : `${t('events:list.create', { defaultValue: 'Create event' })} · ${SHORTCUT_HINTS.add}`,
        variant: 'filled',
        onClick: goToCreate,
      },
    ],
    [isMobile, loading, reload, goToCreate, t],
  );

  // ⌘I create · ⌘R refresh. No search or filters on this page.
  useEntityPageShortcuts({
    onAdd: goToCreate,
    onRefresh: reload,
    canRefresh: !loading,
  });

  const shortcutHints = useEntityPageShortcutHints({ hasAdd: true, hasRefresh: true });

  const handlePullToRefresh = useCallback(async () => {
    await reload();
  }, [reload]);

  const greeting = user?.firstName || user?.username || '';

  const renderBody = () => {
    if (isCrewSession) {
      return <CrewHome eventId={CrewSessionService.getEventId()} onGo={goTo} />;
    }

    // ⚠ The FIRST read only — the read is gated on the resolved user, so `eventsLoading` is still
    // false on the first renders and the first-run screen below used to paint before it.
    // ▶ npm run check-list-states
    if (!eventsLoaded) {
      return <HomeGridSkeleton />;
    }

    // ⚠ FIRST RUN IS ONE ACTION, NOT A DASHBOARD OF EMPTY CARDS. Nothing here has anything to say
    // until there is an event, and a grid of zeroes is the worst possible first impression.
    if (events.length === 0) {
      return (
        <Stack gap="md">
          <HomeFirstRun onCreate={goToCreate} />
          {showChecklist && (
            <ChecklistWidget steps={checklist} publishEventId={null} onGo={goTo} onDismiss={dismissChecklist} />
          )}
        </Stack>
      );
    }

    return (
      <Grid gutter="md">
        {spotlight.event && (
          <Grid.Col span={12}>
            <HomeSpotlight
              event={spotlight.event as Event}
              kind={spotlight.kind}
              stats={spotlightStats}
              statsLoading={loading && !spotlightStats}
              onGo={goTo}
            />
          </Grid.Col>
        )}

        <Grid.Col span={HALF}>
          <AttentionWidget summary={attentionSummary} refreshing={refreshing} onGo={goTo} onRetry={reload} />
        </Grid.Col>

        <Grid.Col span={HALF}>
          <HomeTasksWidget tasks={tasks} loading={tasksLoading} isLoaded={tasksLoaded} eventTitles={eventTitles} onGo={goTo} />
        </Grid.Col>

        <Grid.Col span={HALF}>
          <UpcomingWidget events={otherUpcoming} loading={eventsLoading} isLoaded={eventsLoaded} onOpen={openEvent} onViewAll={goToEvents} />
        </Grid.Col>

        {/* ⚠ Rendered only when there IS a forecast. An empty weather card says "we could not find
            out" in the shape of "nothing to report", which is the failure this page is built around. */}
        {homeWeather?.forecast && (
          <Grid.Col span={HALF}>
            <HomeWeatherWidget weather={homeWeather} />
          </Grid.Col>
        )}

        {showChecklist && (
          <Grid.Col span={HALF}>
            <ChecklistWidget
              steps={checklist}
              publishEventId={spotlight.event?.id ?? null}
              onGo={goTo}
              onDismiss={dismissChecklist}
            />
          </Grid.Col>
        )}
      </Grid>
    );
  };

  return (
    <PageChrome mobileHeaderActions={isCrewSession ? undefined : mobileHeaderActions}>
      <Stack gap="md" style={{ height: '100%' }}>
        <Group justify="space-between" wrap="wrap" gap="sm">
          <Stack gap={0}>
            <Title order={3}>
              {t('home:welcome', { defaultValue: `Welcome${greeting ? ', ' + greeting : ''}`, name: greeting })}
            </Title>
            <Text size="md" c="dimmed">
              {t('home:subtitle', { defaultValue: 'Here’s what needs you today.' })}
            </Text>
          </Stack>
        </Group>

        <ErrorAlert
          error={eventsError}
          title={t('home:loadError', { defaultValue: 'Couldn’t load your events' })}
          onDismiss={reload}
        />

        {/* ⚠ A refresh over content already on screen must be VISIBLE. The first load draws
            skeletons; a re-read drew nothing, so ⌘R and the header's Refresh button looked
            dead. `ContentLoadingOverlay` is the repo's stale-while-revalidating signal. */}
        <ContentLoadingOverlay loading={loading && !!spotlightStats}>
          <PullToRefresh onRefresh={handlePullToRefresh}>{renderBody()}</PullToRefresh>
        </ContentLoadingOverlay>

        <ShortcutHintBar hints={shortcutHints} />
      </Stack>
    </PageChrome>
  );
};
